import { DateTime } from 'luxon';
import moment, { Moment } from 'moment';
import { DD_MM_YYYY, QUOTATION_VALIDATE_MILLISECONDS, YYYY_MM_DD_DATE_FORMAT } from './app-common';
import { RangePickerProps } from 'antd/lib/date-picker';

export const getYear = () => DateTime.now().year;

export const TWO_WEEKS = 14;
export const ONE_WEEK = 7;
export const THREE_MONTH = 3;
export const ONE_MONTH = 1;
export const ADD = '+';
export const SUBTRACT = '-';
export const convertDateFromJSDate = (date: any, format = YYYY_MM_DD_DATE_FORMAT) =>
  date && DateTime.fromJSDate(new Date(date)).toFormat(format);

export const convertDateFromSQLDate = (date: any, format = YYYY_MM_DD_DATE_FORMAT) =>
  date && DateTime.fromSQL(date).toFormat(format);

export const convertDateFromObject = (date: any, format = 'dd-MM-yyyy') =>
  date && DateTime.fromISO(date).toFormat(format);

export const convertDateFromMillis = (date: number | Date | undefined, format = 'dd-MM-yyyy') =>
  date && typeof date === 'number' ? DateTime.fromMillis(date).toFormat(format) : undefined;

export const convertDateFromString = (date: any) => date && DateTime.fromISO(date).toJSDate();

export const getTimeFromObject = (date: any) => date && DateTime.fromISO(date).toObject();

export const getTimeToUnixInteger = (date: any) => date && DateTime.fromISO(date).toUnixInteger();

export const todayDate = () => DateTime.now().toJSDate();

export const oneWeekDate = (days: number = ONE_WEEK) => DateTime.now().minus({ days: days }).toJSDate();

export const todayDateToString = () => DateTime.now().toISODate({ format: 'extended' });

export const dateToString = (days: number) => DateTime.now().minus({ days: days }).toISODate({ format: 'extended' });

export const todayDateObject = () => DateTime.now().toFormat(YYYY_MM_DD_DATE_FORMAT);

export const leadingZero = (value: number) => (value < 10 ? `0${value}` : value);

export const getWeekDate = () => DateTime.now().plus({ days: -7 }).toFormat(YYYY_MM_DD_DATE_FORMAT);

export const monthsDate = (date: Date, months = THREE_MONTH) =>
  DateTime.fromJSDate(date).minus({ months: months }).toJSDate();

export const getCurrentTime = () => DateTime.now().toSQL({ includeOffset: false });
export const convertDateFromInstant = (date: any, format = 'dd-MM-yyyy') => {
  return date ? DateTime.fromMillis(Math.floor(date * 1000)).toFormat(format) : null;
};

export const getZoneName = () => {
  const currentDate = DateTime.now();
  return currentDate?.zoneName;
};

export const getCurrentTimeISO = (date: any) => DateTime.fromISO(date);

export const currentTime = DateTime.now();

export const convertDateIntoMillis: (date: any) => number = (date: any) =>
  DateTime.fromJSDate(new Date(date)).toMillis();

export const onAllconvertDateIntoMillis = (date: any): number => {
  if (!date) return NaN;

  // Moment object
  if (date?._isAMomentObject) {
    return date.valueOf();
  }

  // Already timestamp
  if (typeof date === 'number') {
    return date;
  }

  // JS Date
  if (date instanceof Date) {
    return date.getTime();
  }

  // ISO string or fallback
  return DateTime.fromISO(date).toMillis();
};

export const convertTimeIntoMillis: (date: any) => number = (time: any) => DateTime.fromISO(time).toMillis();

export const convertMillisToDateMoment = (millis?: number | undefined | any) => moment(moment(millis).toDate());

export const getHourMoment = (date?: Moment | number | string, _format: string = 'HH:mm') => moment(date).valueOf();
export const getHoursFromMillis = (data?: number, _format: string = 'HH:mm') => moment(data).minutes().valueOf();
export const getTimeFromMillis = (data?: number, format: string = 'HH:mm') => moment(data).format(format);

export const yesterdayDate = () => moment().add(-1, 'day');
export const weekDate = (num: number) => moment().subtract(num, 'days');
export const momentMonthsDate = (date: Date | Moment, months = THREE_MONTH, add_or_subtract = ADD) =>
  moment(date, DD_MM_YYYY).add(`${add_or_subtract}${months}`, 'months');

export const endOf = () => moment().endOf('day');

export const momentTodayDate = () => moment();

export const millisecondsToMinute = (mins: number) => moment.utc(mins).format('HH:mm:ss');

// //Get the current hour.
// // Add 3 hours to the current hours.
// //Disabled the time from the added hours.
export const disableHours = (date: any, disableHours: number) => {
  const result: number[] = [];
  if (moment(date).isSame(moment(), 'date')) {
    const currentHour = moment().hour();
    const addedTime = currentHour + disableHours;
    for (let i = 0; i <= addedTime; i++) {
      result.push(i);
    }
    return result;
  }
  return result;
};

// Can not select days before today
export const disabledDate: RangePickerProps['disabledDate'] = (current: Moment) => {
  if (!current) return false;
  return current > moment().endOf('days');
};

export const disableFutureDate = (current: Moment) => current && current > moment().endOf('days');

export const disableDateBeforeGivenDate = (currentDate: Moment, givenDate: Moment) => {
  return currentDate && givenDate && currentDate < givenDate.startOf('day');
};

export const disableDateAfterGivenDate = (currentDate: Moment, givenDate: Moment) => {
  return currentDate && givenDate && currentDate > givenDate.endOf('day');
};

export const disablePastDate = (date: Moment) => date && date < moment().startOf('day');

export const skipQuotation = (date?: number | Moment) =>
  date && moment(date).isBefore(moment(QUOTATION_VALIDATE_MILLISECONDS));

export const addLeadingZero = (value: number) => value * 1000;

export const oneDayExpiryTimestamp = Math.round(Date.now() / 1000) + 86400;

export function msToHHmm(ms) {
  const totalMinutes = Math.floor(ms / (1000 * 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

export function msToMinutes(ms) {
  return Math.floor(ms / (1000 * 60));
}

export const formatMinutes = (value: number): string => {
  if (value === null || value === undefined) return '';

  const days = Math.floor(value / (60 * 24));
  const hours = Math.floor((value % (60 * 24)) / 60);
  const minutes = value % 60;

  const result: string[] = []; // 👈 FIX: typed array

  if (days > 0) result.push(`${days} D`);
  if (hours > 0) result.push(`${hours} Hrs`);
  if (minutes > 0) result.push(`${minutes} Min`);

  return result.length > 0 ? result.join(' ') : '0 Min';
};

export const formatDuration = (minutes: number): string => {
  if (minutes === null || minutes === undefined) return "";
  const formatted = moment
    .duration(minutes, 'minutes')
    .format('HH:mm:ss', { trim: false });
  return formatted;
};

export function convertFromUtcIsoStringToLocal(utcIsoString?: string, format: string = 'dd-MM-yyyy HH:mm'): string {
  if (!utcIsoString) return '-';

  const dateTime = DateTime.fromISO(utcIsoString, { zone: 'utc' }).toLocal();

  if (!dateTime.isValid) return '-';

  return dateTime.toFormat(format);
}

